// scheduling/components/pool/CheckInWidget.jsx
// The staff availability signal + capacity signal (§4.1 / §4.2, Trello #257).
//
// Two rules make this widget behave the way it does:
//
//  1. §4.1 as DJB revised it (2026-10-14) — Gen Track staff cannot mark themselves
//     available while the Preparer pool has work; claim it first. Everyone else may
//     always check in. The server decides (canGoAvailable + availabilityBlockedBy);
//     when it says no, the toggle isn't offered and the widget explains why.
//
//  2. The capacity signal is INDEPENDENT of the scheduler's capacity system. Per
//     DJB, someone with 8h scheduled today may still legitimately advertise 2h of
//     pool availability — this is in-the-moment bandwidth, not a derived number.
//     Nothing here reads ScheduleEntries or getDayCap, by design.
//
// There is no expiry: a check-in persists until you toggle off, claim, or are
// assigned work. That's deliberate — "longest waiting first" is the fairness rule,
// so the clock must not restart overnight. Updating your hours does NOT reset it
// either (the server preserves CheckedInAt).

import { useState } from 'react';
import { poolCheckIn, poolCheckOut, poolUpdateCapacity } from '../../services/poolApi';

const QUICK_HOURS = [2, 4, 8];

function fmtWaited(iso) {
  if (!iso) return '';
  const mins = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  if (h < 24) return mins % 60 ? `${h}h ${mins % 60}m` : `${h}h`;
  const d = Math.floor(h / 24);
  return `${d}d ${h % 24}h`;
}

export default function CheckInWidget({ myPools, canGoAvailable, availability, waitingLabel, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [custom, setCustom] = useState('');

  const isAvailable = !!availability;
  const currentHours = availability?.CapacityHours == null ? null : Number(availability.CapacityHours);

  async function run(fn) {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await onChanged();
      setCustom('');
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  if (!myPools || myPools.length === 0) {
    return (
      <div className="sched-pool-side-empty">
        You aren&rsquo;t eligible for any pool, so there&rsquo;s no availability to signal.
      </div>
    );
  }

  // ── Blocked: drain your pools first ──
  if (!isAvailable && !canGoAvailable) {
    return (
      <div className="sched-pool-side-blocked">
        {waitingLabel || 'There’s work waiting in the Preparer pool.'} Gen Track staff
        claim that work before marking themselves available.
      </div>
    );
  }

  return (
    <div className="sched-pool-checkin">
      {isAvailable ? (
        <>
          <div className="sched-pool-checkin-on">
            <span className="sched-pool-checkin-dot" />
            <div>
              <div className="sched-pool-checkin-on-title">You&rsquo;re marked available</div>
              <div className="sched-pool-checkin-on-sub">
                Visible to managers for {fmtWaited(availability.CheckedInAt)}
                {currentHours != null ? ` · ${currentHours}h free` : ' · hours not set'}
              </div>
            </div>
          </div>

          {/* Capacity can be changed without checking out — checking out and back
              in would forfeit your place in the longest-waiting queue. */}
          <div className="sched-pool-checkin-caplabel">How much time do you have?</div>
          <div className="sched-pool-checkin-quick">
            {QUICK_HOURS.map(h => (
              <button
                key={h}
                type="button"
                className={`sched-mgr-modal-btn sched-pool-cap-btn${currentHours === h ? ' sched-pool-cap-btn--on' : ''}`}
                onClick={() => run(() => poolUpdateCapacity(h))}
                disabled={busy}
              >
                {h}h
              </button>
            ))}
            <input
              className="sched-pool-cap-input"
              type="number"
              min="0.25"
              step="0.25"
              placeholder="Other"
              value={custom}
              onChange={e => setCustom(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && custom) run(() => poolUpdateCapacity(Number(custom)));
              }}
              disabled={busy}
            />
            {custom && (
              <button
                type="button"
                className="sched-mgr-modal-btn sched-pool-cap-btn"
                onClick={() => run(() => poolUpdateCapacity(Number(custom)))}
                disabled={busy}
              >
                Set
              </button>
            )}
          </div>
          <div className="sched-pool-checkin-note">
            Updating your hours keeps your place in line.
          </div>

          <button
            type="button"
            className="sched-mgr-modal-btn sched-pool-checkout-btn"
            onClick={() => run(poolCheckOut)}
            disabled={busy}
          >
            {busy ? '…' : 'Check out'}
          </button>
        </>
      ) : (
        <>
          <div className="sched-pool-checkin-off">
            Your pools are clear. Let managers know you have time.
          </div>
          <div className="sched-pool-checkin-caplabel">How much time do you have?</div>
          <div className="sched-pool-checkin-quick">
            {QUICK_HOURS.map(h => (
              <button
                key={h}
                type="button"
                className="sched-mgr-modal-btn sched-pool-cap-btn"
                onClick={() => run(() => poolCheckIn(h))}
                disabled={busy}
              >
                {h}h
              </button>
            ))}
            <input
              className="sched-pool-cap-input"
              type="number"
              min="0.25"
              step="0.25"
              placeholder="Other"
              value={custom}
              onChange={e => setCustom(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && custom) run(() => poolCheckIn(Number(custom)));
              }}
              disabled={busy}
            />
          </div>
          <button
            type="button"
            className="sched-mgr-modal-btn sched-pool-checkin-btn"
            onClick={() => run(() => poolCheckIn(custom ? Number(custom) : null))}
            disabled={busy}
          >
            {busy ? 'Checking in…' : "I'm available"}
          </button>
          <div className="sched-pool-checkin-note">
            Pick an amount above to check in with it, or check in without specifying.
          </div>
        </>
      )}

      {error && <div className="sched-pool-error" style={{ marginTop: 8 }}>{error}</div>}
    </div>
  );
}
